import React, { useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, Users2, Video, Flag, LogOut, ShieldCheck, Menu, X,
  KeyRound, ScrollText,
} from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/admin/dashboard",     label: "Dashboard",     icon: LayoutDashboard, testid: "adm-nav-dashboard" },
  { to: "/admin/profiles",      label: "Profiles",      icon: Users,           testid: "adm-nav-profiles" },
  { to: "/admin/verifications", label: "Verifications", icon: Video,           testid: "adm-nav-verifications" },
  { to: "/admin/users",         label: "Users",         icon: Users2,          testid: "adm-nav-users" },
  { to: "/admin/reports",       label: "Reports",       icon: Flag,            testid: "adm-nav-reports" },
  { to: "/admin/audit-logs",    label: "Audit logs",    icon: ScrollText,      testid: "adm-nav-audit" },
  { to: "/admin/change-password", label: "Change password", icon: KeyRound,    testid: "adm-nav-change-password" },
  // Manage admins — super_admin only, filtered below.
  { to: "/admin/manage",        label: "Manage admins", icon: KeyRound,        testid: "adm-nav-manage", superOnly: true },
];

const ADMIN_ROLES = new Set(["admin", "moderator", "super_admin"]);

/**
 * Guard + shell for /admin/*. Non-admin users are redirected to /admin/login.
 * The public site's Header stays untouched — this layout hides it so the
 * moderation panel feels like a separate application.
 */
export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawer, setDrawer] = useState(false);

  useEffect(() => { setDrawer(false); }, [loc.pathname]);

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-stone-300 text-sm">Loading…</div>;
  if (!user)                     return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  if (!ADMIN_ROLES.has(user.role)) return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;

  const handleLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-stone-100 flex" data-testid="admin-layout">
      {/* SIDEBAR (desktop) / DRAWER (mobile) */}
      <aside className={`
        fixed lg:sticky top-0 left-0 h-screen w-72 lg:w-64 bg-stone-950 border-r border-stone-800
        z-40 transition-transform lg:translate-x-0
        ${drawer ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}>
        <div className="h-16 border-b border-stone-800 flex items-center px-5 justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Relaxora</p>
              <p className="text-sm font-medium">Admin panel</p>
            </div>
          </div>
          <button className="lg:hidden p-1" onClick={() => setDrawer(false)} aria-label="Close menu">
            <X className="w-4 h-4 text-stone-400" />
          </button>
        </div>
        <nav className="p-3 space-y-1">
          {NAV.filter(n => !n.superOnly || user.role === "super_admin").map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition
                ${isActive
                  ? "bg-amber-500/15 text-amber-200 border border-amber-500/30"
                  : "text-stone-300 hover:bg-stone-900 hover:text-stone-100 border border-transparent"}
              `}
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-stone-800">
          <div className="px-2 py-1 mb-2">
            <p className="text-[10px] uppercase tracking-wider text-stone-500">Signed in as</p>
            <p className="text-xs text-stone-200 truncate">{user.email}</p>
            <p className="text-[10px] text-amber-300 uppercase tracking-wider">{user.role}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-700 text-stone-300 hover:bg-stone-900 text-sm"
            data-testid="adm-logout"
          ><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0">
        <header className="lg:hidden h-14 border-b border-stone-800 flex items-center justify-between px-4 bg-stone-950/70 backdrop-blur sticky top-0 z-30">
          <button onClick={() => setDrawer(true)} aria-label="Open menu" data-testid="adm-drawer-open">
            <Menu className="w-5 h-5 text-stone-200" />
          </button>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium">Admin</span>
          </div>
        </header>
        {drawer && <div onClick={() => setDrawer(false)} className="fixed inset-0 bg-black/60 z-30 lg:hidden" />}
        <div className="p-4 sm:p-6 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
